/**
 * The register's services (one set per tab) and the hooks the screens use: who is signed in, what
 * they may do, the register this tablet belongs to (`GET /pos/registers/current`: printer, drawer,
 * open shift, POS rules, feature flags).
 */
import type {
  ConsignmentClient,
  MediaClient,
  OrdersClient,
  PosClient,
  TaxClient,
} from "@tcg/api-client";
import type { RealtimeClient } from "@tcg/realtime";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { createContext, useContext, useSyncExternalStore } from "react";

import {
  call,
  createConsignmentApi,
  createMediaApi,
  createOrdersApi,
  createPosApi,
  createTaxApi,
} from "@/lib/api";
import { AuthController, type AuthState, type IdentityApi, type Staff } from "@/lib/auth";
import { db as defaultDb, type PosDatabase } from "@/lib/db";
import { env } from "@/lib/env";
import { createIdentityApi } from "@/lib/identity";
import { LabelPrinter, PrinterManager } from "@/lib/printer";
import { createSounds, type Sounds } from "@/lib/sound";
import { AvailabilityStore } from "@/realtime/availability";
import { createPosRealtime, createRealtimeScope, type RealtimeScope } from "@/realtime/client";

export interface PosServices {
  db: PosDatabase;
  auth: AuthController;
  api: PosClient;
  /** tax-service's register routes (免税 records — T07-30). */
  tax: TaxClient;
  /** consignment-service's register routes: 本人確認, 古物台帳, 委託受付 (T07-31 / T07-32). */
  consignment: ConsignmentClient;
  /** media-service: ID photos (encrypted, direct), card photos, documents. */
  media: MediaClient;
  /** order-service: 郵便番号 → 住所. */
  orders: OrdersClient;
  printer: PrinterManager;
  /** The label printer (T07-34): ZPL / TSPL bytes or PDFs from pos-service. */
  labels: LabelPrinter;
  sounds: Sounds;
  /** hub + inventory-service + pos-service streams behind one `subscribe()` (T07-40). */
  realtime: RealtimeClient | null;
  /** Register / location the inventory stream is scoped to (filled by the layout). */
  realtimeScope: RealtimeScope;
  /** Live availability of the cards on screen (`inventory.changed:*`). */
  availability: AvailabilityStore;
}

export interface CreateServicesOptions {
  db?: PosDatabase;
  identity?: IdentityApi;
  fetch?: typeof fetch;
  getLocale: () => string;
  /** `false` = no live stream (unit tests). */
  realtime?: boolean;
  /** Gateway origin + `/api` (tests use an absolute URL). */
  baseUrl?: string;
  now?: () => number;
}

export function createServices(options: CreateServicesOptions): PosServices {
  const db = options.db ?? defaultDb;
  const identity =
    options.identity ??
    createIdentityApi({
      getLocale: options.getLocale,
      ...(options.baseUrl ? { baseUrl: options.baseUrl } : {}),
      ...(options.fetch ? { fetch: options.fetch } : {}),
    });
  const auth = new AuthController({ db, identity, ...(options.now ? { now: options.now } : {}) });
  const apiOptions = {
    auth,
    getLocale: options.getLocale,
    ...(options.baseUrl ? { baseUrl: options.baseUrl } : {}),
    ...(options.fetch ? { fetch: options.fetch } : {}),
  };
  const api = createPosApi(apiOptions);
  const tax = createTaxApi(apiOptions);
  const consignment = createConsignmentApi(apiOptions);
  const media = createMediaApi(apiOptions);
  const orders = createOrdersApi(apiOptions);
  const realtimeScope = createRealtimeScope();
  const realtime =
    options.realtime === false
      ? null
      : createPosRealtime({
          bearer: () => auth.bearer(),
          scope: realtimeScope,
          hubBaseUrl: env.VITE_REALTIME_BASE_URL,
          inventoryPath: env.VITE_POS_INVENTORY_STREAM_PATH,
          posEventsPath: env.VITE_POS_EVENTS_PATH,
          ...(options.fetch ? { fetch: options.fetch } : {}),
        });
  return {
    db,
    auth,
    api,
    tax,
    consignment,
    media,
    orders,
    printer: new PrinterManager(db),
    labels: new LabelPrinter(db),
    sounds: createSounds(),
    realtime,
    realtimeScope,
    availability: new AvailabilityStore(options.now),
  };
}

const ServicesContext = createContext<PosServices | null>(null);

export const ServicesProvider = ServicesContext.Provider;

export function usePos(): PosServices {
  const services = useContext(ServicesContext);
  if (!services) {
    throw new Error("usePos() outside <ServicesProvider>");
  }
  return services;
}

export function useAuthState(): AuthState {
  const { auth } = usePos();
  return useSyncExternalStore(auth.subscribe, auth.getSnapshot, auth.getSnapshot);
}

export function useStaff(): Staff | null {
  const state = useAuthState();
  return state.kind === "unlocked" ? state.staff : state.kind === "locked" ? state.lastStaff : null;
}

/** Does the signed-in staff member hold this permission (`shop_common.auth.Permission`)? */
export function useCan(permission: string): boolean {
  const state = useAuthState();
  return state.kind === "unlocked" && state.staff.permissions.includes(permission);
}

export type CurrentRegister = Awaited<ReturnType<typeof fetchRegister>>;

async function fetchRegister(api: PosClient) {
  return call(() => api.GET("/v1/pos/registers/current"));
}

export const registerKey = ["registers", "detail", "current"] as const;

export function useRegister(): UseQueryResult<CurrentRegister> {
  const { api } = usePos();
  const state = useAuthState();
  const paired = state.kind === "locked" || state.kind === "unlocked";
  return useQuery({
    queryKey: registerKey,
    queryFn: () => fetchRegister(api),
    enabled: paired,
    staleTime: 30_000,
  });
}
